import type { CoreMembership, UserLabel } from '@/lib/zifekCore';

interface Props {
  memberships: CoreMembership[];
  users: Record<number, UserLabel>;
}

const ROLE_STYLE: Record<string, string> = {
  owner: 'bg-primary-100 text-primary-900',
  admin: 'bg-primary-100 text-primary-900',
  manager: 'bg-secondary-100 text-secondary-900',
  accountant: 'bg-secondary-100 text-secondary-900',
  vendor: 'bg-accent-100 text-accent-900',
  employee: 'bg-background-100 text-foreground-700',
};

export default function MembershipsPanel({ memberships, users }: Props) {
  if (memberships.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-group-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun membre.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-background-100 text-left text-xs text-foreground-500">
              <th className="px-4 py-3 font-medium">Utilisateur</th>
              <th className="px-4 py-3 font-medium">Organisation</th>
              <th className="px-4 py-3 font-medium">Rôle</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Depuis</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => {
              const u = users[m.user_id];
              return (
                <tr key={m.id} className="border-t border-background-200/60">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground-900 truncate">
                        {u?.name || `Utilisateur #${m.user_id}`}
                        {m.is_primary && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium">Principal</span>
                        )}
                      </div>
                      <div className="text-xs text-foreground-400 truncate">
                        {u?.user_name ? `@${u.user_name}` : ''}{u?.email ? ` · ${u.email}` : ''}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-600">
                    {m.core_organizations?.commercial_name || `Org #${m.organization_id ?? '—'}`}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${ROLE_STYLE[m.role] || 'bg-background-100 text-foreground-700'}`}>
                      {m.role_label || m.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status === 'active' ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                      {m.status === 'active' ? 'Actif' : m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-500 text-xs">
                    {m.joined_at ? new Date(m.joined_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}